import { ChangeDetectorRef, Directive, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { TimeoutService } from '@df/ng/timeout.service';
import { Basket } from '@df/session/api/basket/basket';
import { BasketService } from '@df/session/api/basket/basket.service';
import { Customer } from '@df/session/api/customer';
import { Nexus } from '@tomandco/nexus-api-types';
import { Subscription } from 'rxjs';

export interface IAddToBasketErrorEvent {
    request: Nexus.Lib.Basket.BasketRequestData[];
    error: any;
}

@Directive()
export abstract class ProductAddToBasketComponentDef implements OnInit, OnDestroy {
    /**
     * Product id
     */
    @Input()
    set productId(id: number | undefined) {
        this.product = id ? (this.app.resources.product.getExisting(id) as Product<IProductDataV2>) : undefined;
    }

    /**
     * Product model
     */
    @Input()
    product?: Product<IProductDataV2> | undefined;

    /**
     * Qty to add (in single product mode)
     */
    @Input()
    qty?: number;

    /**
     * Variant for varaiants type product
     */
    @Input()
    variantId?: number | string;

    /**
     * Buy request (in single product mode)
     */
    @Input()
    buyRequest?: Nexus.Lib.Basket.BuyRequest;

    /**
     * Full request (in many items mode)
     */
    @Input()
    basketRequest?: Nexus.Lib.Basket.BasketRequestData | Nexus.Lib.Basket.BasketRequestData[];

    /**
     * Optional on add to basket callback
     */
    @Output()
    success = new EventEmitter();

    /**
     * Optional on add to basket error callback
     */
    @Output()
    error = new EventEmitter<IAddToBasketErrorEvent>();

    /**
     * Customer basket
     * @type {Basket}
     */
    basket: Basket;

    /**
     * Submission status
     */
    status = new Status();

    /**
     * Basket service
     */
    protected basketService = inject(BasketService);

    readonly app = inject(App);

    protected timeoutService = inject(TimeoutService);

    submit: () => void;

    protected subscriptions = new Subscription();

    constructor(
        customer: Customer,
        protected changeDetectorRef: ChangeDetectorRef
    ) {
        this.submit = this.timeoutService.throttle(() => this._submit(), 300);
        this.basket = customer.basket;
    }

    ngOnInit(): void {
        this.subscriptions.add(this.status.subscribe(() => this.detectChanges()));
        this.subscriptions.add(this.basket.subscribe(() => this.detectChanges()));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    /**
     * Notify success output
     */
    emitSuccess() {
        this.success.emit();
    }

    /**
     * Notify error output
     */
    emitError(errorEvent: IAddToBasketErrorEvent) {
        this.error.emit(errorEvent);
    }

    /**
     * Submission
     */
    _submit() {
        const request = this.getRequest()!;
        this.basket.add(
            this.status,
            request,
            result => this.onSuccess(result),
            error => {
                this.onError({ request, error });
            }
        );
    }

    protected onSuccess(result?: any): any {
        this.emitSuccess();
        return result;
    }

    protected onError(errorEvent: IAddToBasketErrorEvent): void {
        this.emitError(errorEvent);
    }

    /**
     * Normalizes whatever we passed here to Nexus.Lib.Basket.BasketRequestData[]
     */
    protected getRequest(): Nexus.Lib.Basket.BasketRequestData[] | undefined {
        const requests = this.basket.normalizeAddToBasketData(
            this.basketRequest || {
                id: this.product ? this.product.id : 0,
                qty: this.qty || 1,
                variantId: this.variantId,
                options: this.buyRequest ? this.buyRequest.options : undefined,
                bundle_options: this.buyRequest ? this.buyRequest.bundle_options : undefined
            }
        );
        if (requests) {
            return requests.filter(i => i.qty > 0);
        }
        return requests;
    }
    /**
     * Checks if all salable and if request is ready
     */
    get isSalableAndReady() {
        const request = this.getRequest();
        return request && request.length && this.isSalable;
    }

    /**
     * Checks if product is salable for given qty (in stock + backorders)
     * NOTE THAT HERE WE CHECK INCLUDING EXISTING BASKET CONTENTS
     */
    get isSalable() {
        return this.basketService.isSalable(this.getRequest());
    }

    /**
     * Checks if product is in stock (skipping backorders) for given qty
     * NOTE THAT HERE WE CHECK INCLUDING EXISTING BASKET CONTENTS
     */
    get isInStock(): boolean {
        return this.basketService.isInStock(this.getRequest());
    }

    /**
     * Returns max selable qty
     * NOTE THAT HERE WE CHECK INCLUDING EXISTING BASKET CONTENTS
     * @return {number}
     */
    get maxQty(): number {
        return this.basketService.maxQty(this.getRequest(), this.qty || 1);
    }
    /**
     * Returns max in-stock qty (skipping backorders)
     * NOTE THAT HERE WE CHECK INCLUDING EXISTING BASKET CONTENTS
     * @return {number}
     */
    get maxInQty(): number {
        return this.basketService.maxInQty(this.getRequest(), this.qty || 1);
    }

    protected detectChanges(): void {
        this.changeDetectorRef.detectChanges();
    }
}
